import React from "react";
import { Checkbox as RadixCheckbox } from "radix-ui";
import { Check } from "lucide-react";
import { cn } from "@/utils/cn";

export type CheckboxProps = React.ComponentPropsWithoutRef<
  typeof RadixCheckbox.Root
>;

// No onCheckedChange — the parent row's onClick is the single source of
// truth for toggling. That's safe (not a double-toggle risk) because Radix's
// Checkbox.Root renders a real <button role="checkbox"> that doesn't call
// stopPropagation() unless it's form-associated (ours isn't), so exactly one
// click event bubbles up to the row whether it's a mouse click landing
// directly on the checkbox or a Space/Enter press while it's focused — this
// is what gives keyboard users the same "Tab to a row's checkbox, press
// Space to toggle" path the original MUI <input type="checkbox"> had for
// free. Do not add tabIndex={-1} here — that was tried and it silently
// removed every row from the tab order.
const Checkbox = React.forwardRef<HTMLButtonElement, CheckboxProps>(
  ({ checked, className = "", ...props }, ref) => (
    <RadixCheckbox.Root
      ref={ref}
      checked={checked}
      className={cn(
        "flex h-4 w-4 shrink-0 items-center justify-center rounded-[4px] border border-slate-300 transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 data-[state=checked]:scale-105 data-[state=checked]:border-primary data-[state=checked]:bg-primary data-[state=checked]:ring-[3px] data-[state=checked]:ring-primary/15",
        className
      )}
      {...props}
    >
      <RadixCheckbox.Indicator
        forceMount
        className="scale-50 opacity-0 transition-all duration-150 data-[state=checked]:scale-100 data-[state=checked]:opacity-100"
      >
        <Check size={12} className="text-white" strokeWidth={3} />
      </RadixCheckbox.Indicator>
    </RadixCheckbox.Root>
  )
);
Checkbox.displayName = "Checkbox";

export default Checkbox;
